import {
  AggregatedInvoice,
  AggregatedLineItemGroup,
  ServerLineItem
} from '../types'
import differenceBy from 'lodash/differenceBy'
import { useMutation } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useCallback } from 'react'
import { dequal } from 'dequal'
import { pick } from 'lodash/fp'
import { invoice } from 'Invoices/ports'
import { useInvalidateInvoice } from './useInvalidateInvoice'
import { isDemoEnv } from 'lib/environment/environment'

export const useSaveAggregatedInvoice = (invoiceId: string) => {
  const invalidateInvoice = useInvalidateInvoice()

  const putInvoiceMutation = useMutation(dashboardv99990101Client.putInvoice)
  const patchSentInvoiceMutation = useMutation(
    dashboard20240730Client.patchSentInvoice,
    { invalidateQueries: () => [] }
  )

  const postLineItemMutation = useMutation(
    dashboardv99990101Client.postLineItem,
    { invalidateQueries: () => [] }
  )

  const putLineItemMutation = useMutation(
    dashboardv99990101Client.putLineItem,
    { invalidateQueries: () => [] }
  )

  const deleteLineItemGroupMutation = useMutation(
    dashboardv99990101Client.deleteLineItemGroup,
    { invalidateQueries: () => [] }
  )

  const deleteLineItemMutation = useMutation(
    dashboardv99990101Client.deleteLineItem,
    { invalidateQueries: () => [] }
  )

  const postLineItemGroupMutation = useMutation(
    dashboardv99990101Client.postLineItemGroup,
    { invalidateQueries: () => [] }
  )

  const putLineItemGroupMutation = useMutation(
    dashboardv99990101Client.putLineItemGroup,
    { invalidateQueries: () => [] }
  )

  // returns true if any updates were made
  const performLineItemGroupSave = useCallback(
    async (lineItemGroupDiffs: ReturnType<typeof diffLineItemGroups>) => {
      const groupAdditions = lineItemGroupDiffs.groupsToAdd.map(async item => {
        const newLineItemGroup = await postLineItemGroupMutation.mutateAsync({
          invoiceId,
          body: item
        })

        if (!newLineItemGroup) {
          throw new Error()
        }

        for (const lineItem of item.lineItems) {
          await postLineItemMutation.mutateAsync({
            invoiceId,
            body: {
              ...lineItem,
              groupId: newLineItemGroup.id
            }
          })
        }
      })

      const lineItemDeletions = lineItemGroupDiffs.lineItemsToDelete.map(id =>
        deleteLineItemMutation.mutateAsync({ lineItemId: id, invoiceId })
      )

      const lineItemAdditions = lineItemGroupDiffs.lineItemsToAdd.map(item => {
        // remove client id
        const { id, ...itemToAdd } = item
        return postLineItemMutation.mutateAsync({
          body: itemToAdd,
          invoiceId
        })
      })

      const groupDeletions = lineItemGroupDiffs.groupsToDelete.map(id =>
        deleteLineItemGroupMutation.mutateAsync({
          lineItemGroupId: id,
          invoiceId
        })
      )

      const asyncActions = [
        ...lineItemGroupDiffs.groupsToUpdate,
        ...lineItemGroupDiffs.lineItemsToUpdate
      ]

      /**
       * There's concurrency issues that cause updated line items and groups to have incorrect data if lots of
       * requests are made at the same time (particularly when updating indexes), so run these updates one after the other
       */
      for (const body of lineItemGroupDiffs.lineItemsToUpdate) {
        await putLineItemMutation.mutateAsync({
          invoiceId,
          id: body.id,
          body
        })
      }

      for (const body of lineItemGroupDiffs.groupsToUpdate) {
        await putLineItemGroupMutation.mutateAsync({
          invoiceId,
          id: body.id,
          body
        })
      }

      const concurrentActions = [
        ...lineItemDeletions,
        ...lineItemAdditions,
        ...groupAdditions,
        ...groupDeletions
      ]

      await Promise.all(concurrentActions)

      const totalActions = concurrentActions.length + asyncActions.length

      return totalActions > 0
    },
    [
      invoiceId,
      deleteLineItemMutation,
      deleteLineItemGroupMutation,
      postLineItemMutation,
      putLineItemMutation,
      postLineItemGroupMutation,
      putLineItemGroupMutation
    ]
  )

  return useCallback(
    async (
      serverInvoice: AggregatedInvoice,
      clientInvoice: AggregatedInvoice
    ) => {
      if (isDemoEnv()) {
        return
      }

      if (clientInvoice.status === 'SENT') {
        const hasPurchaseOrderNumberChange =
          serverInvoice.purchaseOrderNumber !==
          clientInvoice.purchaseOrderNumber

        const hasCustomerBillingAddressChange = !dequal(
          serverInvoice.customerBillingAddress,
          clientInvoice.customerBillingAddress
        )

        const hasMemoChange = serverInvoice.memo !== clientInvoice.memo

        const hasReferenceChange =
          serverInvoice.reference !== clientInvoice.reference

        if (
          hasPurchaseOrderNumberChange ||
          hasCustomerBillingAddressChange ||
          hasMemoChange ||
          hasReferenceChange
        ) {
          await patchSentInvoiceMutation.mutateAsync({
            id: invoiceId,
            body: {
              ...(hasPurchaseOrderNumberChange && {
                purchaseOrderNumber: clientInvoice.purchaseOrderNumber
              }),
              ...(hasCustomerBillingAddressChange &&
                clientInvoice.customerBillingAddress && {
                  customerBillingAddress: clientInvoice.customerBillingAddress
                }),
              ...(hasMemoChange && {
                memo: clientInvoice.memo
              }),
              ...(hasReferenceChange && {
                reference: clientInvoice.reference
              })
            }
          })
        }

        return
      }

      const lineItemGroupDiffs = diffLineItemGroups(
        serverInvoice.lineItemGroups,
        clientInvoice.lineItemGroups
      )

      const didUpdateGroups = await performLineItemGroupSave(lineItemGroupDiffs)

      const hasInvoiceDiffs = !dequal(
        invoice.toServer(serverInvoice),
        invoice.toServer(clientInvoice)
      )

      if (hasInvoiceDiffs) {
        await putInvoiceMutation.mutateAsync({
          body: invoice.toServer(clientInvoice),
          id: invoiceId
        })
      }

      if (didUpdateGroups) {
        invalidateInvoice()
      }
    },
    [
      performLineItemGroupSave,
      putInvoiceMutation,
      patchSentInvoiceMutation,
      invoiceId,
      invalidateInvoice
    ]
  )
}

export const diffLineItemGroups = (
  serverLineItemGroups: AggregatedLineItemGroup[],
  clientLineItemGroups: AggregatedLineItemGroup[]
) => {
  const diffs = {
    groupsToAdd: [] as AggregatedLineItemGroup[],
    groupsToDelete: [] as string[],
    groupsToUpdate: [] as AggregatedLineItemGroup[],
    lineItemsToDelete: [] as string[],
    lineItemsToAdd: [] as ServerLineItem[],
    lineItemsToUpdate: [] as ServerLineItem[]
  }

  for (const clientLineItemGroup of clientLineItemGroups) {
    const existingGroup = serverLineItemGroups.find(
      g => g.id === clientLineItemGroup.id
    )

    if (existingGroup) {
      continue
    }

    diffs.groupsToAdd.push(clientLineItemGroup)
  }

  for (const serverLineItemGroup of serverLineItemGroups) {
    const changedGroup = clientLineItemGroups.find(
      g => g.id === serverLineItemGroup.id
    )

    if (!changedGroup) {
      diffs.groupsToDelete.push(serverLineItemGroup.id)
      continue
    }

    diffs.lineItemsToDelete.push(
      ...differenceBy(
        serverLineItemGroup.lineItems,
        changedGroup.lineItems,
        'id'
      ).map(item => item.id)
    )

    if (
      !dequal(
        lineItemGroupEditableFields(serverLineItemGroup),
        lineItemGroupEditableFields(changedGroup)
      )
    ) {
      diffs.groupsToUpdate.push(changedGroup)
    }

    for (const item of changedGroup.lineItems) {
      const storedItem = serverLineItemGroup.lineItems.find(
        i => i.id === item.id
      )

      if (!storedItem) {
        diffs.lineItemsToAdd.push(item)
        continue
      }

      if (
        !dequal(
          lineItemEditableFields(storedItem),
          lineItemEditableFields(item)
        )
      ) {
        diffs.lineItemsToUpdate.push(item)
      }
    }
  }

  return diffs
}

const lineItemGroupEditableFields = pick([
  'title',
  'description',
  'index',
  /**
   * When updated, changes to these attributes cascade down to line items on the backend
   */
  'taxCategory.id',
  'servicePeriod',
  'revenueRecognitionMethod',
  'revenueClassification'
])

const lineItemEditableFields = pick([
  'title',
  'quantity',
  'rate',
  'rateDisplay',
  'description',
  'revenueRecognitionMethod',
  'externalIds'
])
